// ----------------------------------------------------------------------------
// PLAN CREATE ERROR RESOLUTION
// Turns an Axios-style error from plansApi.create into a discriminated result
// the Roster page can act on: open the plan-limit modal (402), show a friendly
// business-rule toast (400), or fall back to a generic error. Kept pure so the
// branching is easy to unit test without mounting the whole Roster page.
// ----------------------------------------------------------------------------

export type CreatePlanErrorResult =
  | { kind: 'limit'; message: string; maxPlans: number; tier: string }
  | { kind: 'message'; message: string }
  | { kind: 'generic' };

function humanizeTier(raw: unknown): string {
  if (typeof raw !== 'string' || raw.length === 0) return 'current';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/**
 * Map a plan-creation error to a UI action.
 *
 * @param err              The rejected error from plansApi.create (Axios-shaped).
 * @param fallbackMaxPlans Plan count to show if the 402 body omits maxPlans.
 */
export function resolveCreatePlanError(err: any, fallbackMaxPlans: number): CreatePlanErrorResult {
  const status = err?.response?.status;
  const body = err?.response?.data ?? {};

  // 402: subscription plan limit reached.
  if (status === 402) {
    const maxPlans = typeof body.maxPlans === 'number' ? body.maxPlans : fallbackMaxPlans;
    const tier = humanizeTier(body.currentTier);
    const message =
      typeof body.error === 'string' && body.error
        ? body.error
        : `You've reached your limit of ${maxPlans} active service plans on the ${tier} plan. Delete an existing plan or upgrade to create more.`;
    return { kind: 'limit', message, maxPlans, tier };
  }

  // 400: a business rule blocked the create and the server sent a human message.
  if (status === 400 && typeof body.error === 'string' && body.error) {
    return { kind: 'message', message: body.error };
  }

  return { kind: 'generic' };
}
